import { Popconfirm, Radio, Space } from 'antd';
import React, { useEffect, useMemo, useState } from 'react';
import { toCurrency, toRate } from 'utils/utils';
import DataGrid from 'components/DataGrid';
import DeleteButton from 'components/DeleteButton';
import ScheduleButton from 'components/ScheduleButton';
import IfrsScheduleModal from 'components/IfrsScheduleModal';
import { DataGridColDef } from 'components/DataGrid/DataGrid';
import SpreadingScheduleModal from 'components/SpreadingScheduleModal';
import { FormInstance } from 'antd/lib/form';
import FinancialScheduleModal from 'components/FinancialScheduleModal';
import SimpleFinElementModal from './SimpleFinElementModal';
import ModalControllerNV from 'components/ModalControllerNV';
import { dateToString } from 'utils/utils2';
import AdvancedModeModal from './AdvancedModeModal';

export type ElementsFinTableProps = {
  display?: boolean;
  hideDelete?: boolean;
  onDeleteElement: (element: any) => void;
  elements: any[];
  contract?: any;
  offerid?: any;
  prcode: string;
  form?: FormInstance;
  onSave?: (element: any, allElements: any, action: string) => void;
  onSelect: (cteid: number) => void;
  oldElements?: any[];
  preFin?: boolean;
  ctfclevel?: string;
  assetAmount?: number;
  totalFinAmountElemLinkedToAsset?: number;
  flagretailer?: boolean;
};

const ElementsFinTable = (props: ElementsFinTableProps) => {
  const {
    onDeleteElement,
    elements,
    onSelect,
    onSave,
    oldElements,
    offerid,
    prcode,
    display,
    form,
    hideDelete,
    preFin,
    ctfclevel,
    assetAmount,
    totalFinAmountElemLinkedToAsset = 0,
    flagretailer,
  } = props;
  const [selectedFIN, setselectedFIN] = useState(elements[0]?.cteid ?? 0);
  useEffect(() => {
    if (elements && form) {
      setselectedFIN(form?.getFieldValue('selectedElement'));
    }
  }, [elements, form]);

  const columns = useMemo<DataGridColDef<any>[]>(
    () => [
      {
        comcode: '_',
        dataIndex: '_',
        render: (value, record) => (
          <Radio
            checked={selectedFIN === record?.cteid}
            onClick={() => handleSelectFin(record)}
          ></Radio>
        ),
        width: 30,
      },
      {
        comcode: 'Element.cteid',
        dataIndex: 'cteid',
        width: 'hide',
        render: (value, record) => null,
      },
      {
        comcode: 'Element.ctecategorylabel',
        dataIndex: 'ctecategorylabel',
        width: '150px',
        render: (value, record) => (
          <ModalControllerNV
            content={({ setVisible, visible }) => (
              <a
                onClick={() => {
                  setVisible(true);
                }}
              >
                {value ?? record.ctecategory}
              </a>
            )}
            modal={({ setVisible, visible }) => (
              <SimpleFinElementModal
                visible={visible}
                onClose={() => setVisible(false)}
                form={form!}
                display
                onSave={onSave!}
                finElement={record}
                updateElement
                offerParams={form?.getFieldValue('offerParams')}
                ctfclevel={ctfclevel}
                assetAmount={assetAmount}
                elements={elements}
                totalFinAmountElemLinkedToAsset={
                  totalFinAmountElemLinkedToAsset
                }
              />
            )}
          />
        ),
      },

      {
        comcode: 'Element.ctedescription',
        dataIndex: 'ctedescription',
        width: '200px',
        render: (ctedescription, record) => (
          <ModalControllerNV
            content={({ setVisible, visible }) => (
              <a
                onClick={() => {
                  setVisible(true);
                }}
              >
                {ctedescription}
              </a>
            )}
            modal={({ setVisible, visible }) => (
              <SimpleFinElementModal
                visible={visible}
                onClose={() => setVisible(false)}
                form={form!}
                display
                onSave={onSave!}
                finElement={record}
                updateElement
                offerParams={form?.getFieldValue('offerParams')}
                assetAmount={assetAmount}
                ctfclevel={ctfclevel}
              />
            )}
          />
        ),
      },
      {
        comcode: 'Element.ofrlabel',
        dataIndex: 'ofrlabel',
        width: '150px',
      },
      {
        comcode: 'Element.cteinvestamount',
        dataIndex: 'cteinvestamount',
        render: (value, record) =>
          toCurrency(value, form?.getFieldValue('currcode')),
        width: '150px',
        align: 'right',
      },
      {
        comcode: 'Element.ctenominalrate',
        dataIndex: 'ctenominalrate',
        width: '100px',
        render: (val, record) =>
          record?.ctecalculationtype === 'PREFIN'
            ? toRate(record?.prefinnominalrate)
            : toRate(val),
      },
      {
        comcode: 'Element.cteinstallmentamount',
        dataIndex: 'cteinstallmentamount',
        render: (value, record) =>
          toCurrency(value, form?.getFieldValue('currcode')),
        width: '100px',
        align: 'right',
      },
      {
        comcode: 'Element.oldctefinancedamount',
        dataIndex: 'oldctefinancedamount',
        render: (value, record) =>
          toCurrency(
            oldElements?.find((ele) => ele.cteid === record.cteid)
              ?.ctefinancedamount,
            form?.getFieldValue('currcode')
          ),
        width: '150px',
        align: 'right',
      },
      {
        comcode: 'Element.oldcteduration',
        dataIndex: 'oldcteduration',
        render: (value, record) =>
          oldElements?.find((ele) => ele.cteid === record.cteid)?.cteduration,
        width: '100px',
        align: 'center',
      },

      {
        comcode: 'Element.oldcterentalamount',
        dataIndex: 'oldcterentalamount',
        render: (value, record) =>
          toCurrency(
            oldElements?.find((ele) => ele.cteid === record.cteid)
              ?.cterentalamount,
            form?.getFieldValue('currcode')
          ),
        width: '100px',
        align: 'right',
      },
      {
        comcode: 'Element.ctefinancedamount',
        dataIndex: 'ctefinancedamount',
        render: (value, record) =>
          toCurrency(value, form?.getFieldValue('currcode')),
        width: '150px',
        align: 'right',
      },
      {
        comcode: 'Element.cteduration',
        dataIndex: 'cteduration',
        width: '100px',
        align: 'center',
      },

      {
        comcode: 'Element.cterentalamount',
        dataIndex: 'cterentalamount',
        render: (value, record) =>
          toCurrency(value, form?.getFieldValue('currcode')),
        width: '100px',
        align: 'right',
      },

      {
        dataIndex: 'ctestatuslabel',
        width: '100px',
        align: 'right',
      },
      {
        dataIndex: 'ctestartdate',
        width: '100px',
        align: 'right',
      },

      {
        dataIndex: 'cteenddate',
        width: '100px',
        align: 'right',
      },

      {
        comcode: 'actions',
        key: 'operation',
        render: (text, record) => (
          <Space size="small">
            {!display && !hideDelete && (
              <Popconfirm
                title="Do you want to delete this element?"
                onConfirm={() => onDeleteElement(record)}
              >
                <DeleteButton onlyIcon />
              </Popconfirm>
            )}
            <FinancialScheduleModal
              currcode={form?.getFieldValue('currcode')}
              finElement={record}
              comcode="finSchedule"
              flagretailer={flagretailer}
              controller={
                <ScheduleButton
                  size="large"
                  comcode="finSchedule"
                  twoToneColor="blue"
                ></ScheduleButton>
              }
            />
            {!display ? (
              <>
                {record.conElemSpreading?.length > 0 && (
                  <SpreadingScheduleModal
                    financialSchedule={record.conElemSpreading}
                    controller={
                      <ScheduleButton
                        comcode="SpreadingScheduleButton"
                        twoToneColor="red"
                        style={{
                          height: 40,
                          marginLeft: 5,
                          marginRight: 5,
                          marginBottom: 5,
                        }}
                        onlyicon
                      />
                    }
                  />
                )}
                {record.conElemIFRS?.length > 0 && (
                  <IfrsScheduleModal
                    financialSchedule={record.conElemIFRS}
                    controller={
                      <ScheduleButton
                        comcode="IfrsScheduleButton"
                        twoToneColor="green"
                        style={{
                          height: 40,
                          marginLeft: 5,
                          marginRight: 5,
                          marginBottom: 5,
                          // zIndex:4
                        }}
                        onlyicon
                      />
                    }
                  />
                )}
              </>
            ) : null}
          </Space>
        ),
        fixed: 'right',
        width: '200px',
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [form, offerid, onDeleteElement, onSave, prcode, assetAmount, selectedFIN]
  );

  const columnsPreFin = useMemo<DataGridColDef<any>[]>(
    () => [
      {
        comcode: 'Element.ctedescription',
        dataIndex: 'ctedescription',
        fixed: 'left',
        width: '250px',
        render: (ctedescription, record) =>
          record.ctecalculationtype !== 'PREFIN' ? (
            <ModalControllerNV
              content={({ setVisible, visible }) => (
                <a onClick={() => setVisible(true)}>{ctedescription}</a>
              )}
              modal={({ setVisible, visible }) => (
                <SimpleFinElementModal
                  visible={visible}
                  onClose={() => setVisible(false)}
                  form={form!}
                  display
                  onSave={onSave!}
                  finElement={record}
                  updateElement
                  offerParams={form?.getFieldValue('offerParams')}
                />
              )}
            />
          ) : (
            <ModalControllerNV
              content={({ setVisible, visible }) => (
                <a onClick={() => setVisible(true)}>{ctedescription}</a>
              )}
              modal={({ setVisible, visible }) => (
                <AdvancedModeModal
                  visible={visible}
                  onClose={() => setVisible(false)}
                  form={form!}
                  preFin={record.ctecalculationtype === 'PREFIN'}
                  cteid={record.cteid}
                />
              )}
            />
          ),
      },
      {
        comcode: 'Element.ofrlabel',
        dataIndex: 'ofrlabel',
        width: '150px',
      },
      {
        comcode: 'Element.cteinvestamount',
        dataIndex: 'cteinvestamount',
        render: (value, record) =>
          toCurrency(value, form?.getFieldValue('currcode')),
        width: '150px',
        align: 'right',
      },

      {
        dataIndex: 'prefinnominalrate',
        render: (val, record) =>
          record?.ctecalculationtype === 'PREFIN'
            ? toRate(val)
            : toRate(record.ctenominalrate),
        width: '150px',
        align: 'right',
      },
      {
        dataIndex: 'cteduration',
        width: '100px',
        align: 'center',
      },
      {
        dataIndex: 'prefinperiodicity',
        width: '150px',
        align: 'right',
      },
      {
        dataIndex: 'prefinmethod',
        width: '100px',
        align: 'center',
      },
      {
        dataIndex: 'ctestartdate',
        width: '150px',
        render: (val) => dateToString(val),
        align: 'center',
        fixed: 'right',
      },
      {
        dataIndex: 'cteenddate',
        width: '100px',
        render: (val) => dateToString(val),
        fixed: 'right',
        align: 'center',
      },
      {
        dataIndex: 'ctestatus',
        width: '100px',
        align: 'center',
        fixed: 'right',
      },
      {
        comcode: 'actions',
        key: 'operation',
        render: (text, record) => (
          <Space size="small">
            {!display && !hideDelete && (
              <Popconfirm
                title="Do you want to delete this element?"
                onConfirm={() => onDeleteElement(record)}
              >
                <DeleteButton onlyIcon />
              </Popconfirm>
            )}
            <FinancialScheduleModal
              currcode={form?.getFieldValue('currcode')}
              finElement={record}
              comcode="finSchedule"
              flagretailer={flagretailer}
              controller={
                <ScheduleButton
                  size="large"
                  comcode="finSchedule"
                  twoToneColor="blue"
                ></ScheduleButton>
              }
            />
            {!display ? (
              <>
                {record.conElemSpreading?.length > 0 && (
                  <SpreadingScheduleModal
                    financialSchedule={record.conElemSpreading}
                    controller={
                      <ScheduleButton
                        comcode="SpreadingScheduleButton"
                        twoToneColor="red"
                        style={{
                          height: 40,
                          marginLeft: 5,
                          marginRight: 5,
                          marginBottom: 5,
                        }}
                        onlyicon
                      />
                    }
                  />
                )}
                {record.conElemIFRS?.length > 0 && (
                  <IfrsScheduleModal
                    financialSchedule={record.conElemIFRS}
                    controller={
                      <ScheduleButton
                        comcode="IfrsScheduleButton"
                        twoToneColor="green"
                        style={{
                          height: 40,
                          marginLeft: 5,
                          marginRight: 5,
                          marginBottom: 5,
                          // zIndex:4
                        }}
                        onlyicon
                      />
                    }
                  />
                )}
              </>
            ) : null}
          </Space>
        ),
        fixed: 'right',
        width: '200px',
      },
    ],
    [form, offerid, onDeleteElement, onSave, prcode]
  );

  const getColumns = () => {
    return preFin
      ? columnsPreFin
      : !display
      ? columns.filter(
          (col) =>
            col.dataIndex !== 'cteid' &&
            col.dataIndex !== 'oldctefinancedamount' &&
            col.dataIndex !== 'oldcteduration' &&
            col.dataIndex !== 'oldcterentalamount'
        )
      : columns.filter(
          (col) =>
            col.dataIndex !== 'ctecategorylabel' && col.dataIndex !== 'ofrlabel'
        );
  };

  // const rowSelection = {
  //   onChange: (selectedRowKeys: any, selectedRows: any) => {
  //     const selectedElem = selectedRows[0];

  //

  //     form?.setFieldsValue({ selectedElement: selectedElem.cteid });

  //     onSelect(selectedElem.cteid ?? 0);
  //   },
  // };
  const handleSelectFin = (record: any) => {
    setselectedFIN(record.cteid);
    form?.setFieldValue('selectedElement', record.cteid);
    form?.setFieldValue('ctedescription', record.ctedescription);

    onSelect(record.cteid ?? 0);
  };

  return (
    <DataGrid
      scroll={{ x: 1000, y: 900 }}
      columns={getColumns()}
      rowKey={(element) => element.cteid}
      dataSource={
        elements
          .map((ele) => {
            return {
              key: ele?.cteid,
              ...ele,
            };
          })
          .sort((a, b) => b.cteid - a.cteid) || []
      }
      // rowSelection={{
      //   type: 'radio',
      //   renderCell: (val, record, index) => {
      //     return (
      //       <Button onClick={() => handleSelectFin(record)}>&nbsp;&&&&</Button>
      //     );
      //   },
      //   selectedRowKeys: [selectedFIN],
      //   // ...rowSelection,
      // }}
    />
  );
};

export default ElementsFinTable;
